import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import Header from "../../../../components/Header";
import useAuth from "../../../../auth/useAuth";
import { useFetchGET } from "../../../../state/FetchHelpers";
import {
  automaticRoutesListRoute,
  buildAutomaticRoutes,
  sinAnilloRoute,
  anillosListRoute,
  addCiudadToAnillo,
  addOrderToRoute,
} from "../../../../config";
import { Alerts } from "../../../../components/alerts/Alerts.tsx";
import Loading from "../../../global/Loading";
import FatalError from "../../../global/FatalError";
import RouteCard from "./RouteCard";

const getLocalDate = () =>
  new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

const RuteoAutomatico = () => {
  const auth = useAuth();
  const token = auth.globals.token;
  const navigate = useNavigate();
  const { alertSuccess, showErrorRequest } = Alerts();

  const canAccessRuteo =
    auth.globals.id_role === 1 || auth.globals.id_role === 29 || auth.globals.id_role === 31;

  const [targetDate, setTargetDate] = useState(getLocalDate());
  const [building, setBuilding] = useState(false);
  const [buildResult, setBuildResult] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pendiente_aprobacion");
  const [listDate, setListDate] = useState(getLocalDate());
  const [refreshToggle, setRefreshToggle] = useState(0);

  // Sin-anillo dialog
  const [sinAnilloOpen, setSinAnilloOpen] = useState(false);
  const [sinAnilloData, setSinAnilloData] = useState(null);
  const [loadingSinAnillo, setLoadingSinAnillo] = useState(false);

  // Add ZIP to existing anillo sub-dialog
  const [addZipOpen, setAddZipOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [anillosList, setAnillosList] = useState([]);
  const [loadingAnillos, setLoadingAnillos] = useState(false);
  const [selectedAnilloId, setSelectedAnilloId] = useState("");
  const [addingZip, setAddingZip] = useState(false);

  // Add order to existing route sub-dialog
  const [addToRouteOpen, setAddToRouteOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [targetRouteIdForOrder, setTargetRouteIdForOrder] = useState("");
  const [addingToRoute, setAddingToRoute] = useState(false);

  const { data: routesResponse, loading: routesLoading, error: routesError } = useFetchGET(
    canAccessRuteo ? automaticRoutesListRoute(statusFilter, listDate) : undefined,
    token,
    [],
    refreshToggle
  );

  const { data: routesForOrderResponse, loading: loadingRoutesForOrder } = useFetchGET(
    addToRouteOpen ? automaticRoutesListRoute("pendiente_aprobacion", targetDate) : undefined,
    token,
    []
  );
  const routesForOrder = Array.isArray(routesForOrderResponse?.routes) ? routesForOrderResponse.routes : [];

  const routesListData = Array.isArray(routesResponse?.routes) ? routesResponse.routes : [];
  const refreshRoutes = () => setRefreshToggle((prev) => prev + 1);

  const handleBuildRoutes = async () => {
    if (!targetDate) return;
    try {
      setBuilding(true);
      setBuildResult(null);
      const resp = await buildAutomaticRoutes(token, auth.globals.id, targetDate);
      if (resp.ok) {
        const data = await resp.json();
        setBuildResult(data);
        setListDate(targetDate);
        refreshRoutes();
      } else if (resp.status === 401 || resp.status === 403) {
        showErrorRequest("No tiene autorización o su sesión ha expirado", auth);
      } else {
        const errData = await resp.json().catch(() => null);
        showErrorRequest(errData?.detail || errData?.message || "No fue posible crear las rutas automáticas", auth);
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    } finally {
      setBuilding(false);
    }
  };

  const handleViewSinAnillo = async () => {
    setSinAnilloOpen(true);
    setSinAnilloData(null);
    setLoadingSinAnillo(true);
    try {
      const resp = await fetch(sinAnilloRoute(targetDate), {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (resp.ok) {
        const data = await resp.json();
        setSinAnilloData(data);
      }
    } catch {
      // leave sinAnilloData null
    } finally {
      setLoadingSinAnillo(false);
    }
  };

  const handleOpenAddZip = async (grupo) => {
    setSelectedGroup(grupo);
    setSelectedAnilloId("");
    setAddZipOpen(true);
    setLoadingAnillos(true);
    try {
      const resp = await fetch(anillosListRoute(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setAnillosList(Array.isArray(data?.anillos) ? data.anillos : []);
      }
    } catch {
      setAnillosList([]);
    } finally {
      setLoadingAnillos(false);
    }
  };

  const handleAddZipSubmit = async () => {
    if (!selectedAnilloId || !selectedGroup) return;
    setAddingZip(true);
    try {
      const resp = await addCiudadToAnillo(token, selectedAnilloId, {
        city_name: selectedGroup.city,
        state_name: selectedGroup.state,
        zip_code: selectedGroup.zip,
      });
      if (resp.ok) {
        alertSuccess("Éxito", `ZIP ${selectedGroup.zip} agregado al anillo`);
        setAddZipOpen(false);
      } else {
        const err = await resp.json().catch(() => null);
        showErrorRequest(err?.detail || err?.message || "No fue posible agregar el ZIP", auth);
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    } finally {
      setAddingZip(false);
    }
  };

  const handleOpenAddToRoute = (orden) => {
    setSelectedOrder(orden);
    setTargetRouteIdForOrder("");
    setAddToRouteOpen(true);
  };

  const handleAddToRouteSubmit = async () => {
    if (!targetRouteIdForOrder || !selectedOrder) return;
    setAddingToRoute(true);
    try {
      const resp = await addOrderToRoute(token, targetRouteIdForOrder, selectedOrder.box_id);
      if (resp.ok) {
        alertSuccess("Éxito", `Orden ${selectedOrder.correlative} agregada a la ruta`);
        setAddToRouteOpen(false);
        refreshRoutes();
        handleViewSinAnillo();
      } else if (resp.status === 401 || resp.status === 403) {
        showErrorRequest("No tiene autorización o su sesión ha expirado", auth);
      } else {
        const err = await resp.json().catch(() => null);
        showErrorRequest(err?.detail || err?.message || "No fue posible agregar la orden a la ruta", auth);
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    } finally {
      setAddingToRoute(false);
    }
  };

  if (!canAccessRuteo) {
    return (
      <Box m="20px" marginBottom="64px">
        <Header title="Ruteo Automático" subtitle="Acceso restringido" />
        <Alert severity="warning">
          <AlertTitle sx={{ fontWeight: "bold" }}>Sin permisos</AlertTitle>
          No tienes permisos para acceder a esta sección. Contacta a un administrador si crees que esto es un error.
        </Alert>
      </Box>
    );
  }

  if (routesError) return <FatalError />;

  const grupos = sinAnilloData?.grupos ?? [];

  return (
    <Box m="20px" marginBottom="64px">
      <Header title="Ruteo Automático" subtitle="Generación y aprobación de rutas por proximidad" />

      <Box mb={4}>
        <Typography variant="h6" mb={1}>
          Crear rutas
        </Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Fecha de órdenes"
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            variant="filled"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" color="secondary" disabled={building} onClick={handleBuildRoutes}>
            {building ? <CircularProgress size={20} color="inherit" /> : "Crear Rutas Automáticas"}
          </Button>
          <Button variant="outlined" color="secondary" disabled={!targetDate || loadingSinAnillo} onClick={handleViewSinAnillo}>
            {loadingSinAnillo ? <CircularProgress size={20} color="inherit" /> : "Ver órdenes sin anillo"}
          </Button>
        </Box>

        {building && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            Generando rutas, esto puede tardar varios segundos...
          </Typography>
        )}

        {buildResult && (
          <Box mt={2}>
            <Alert severity="success" sx={{ mb: 1 }}>
              Se crearon {buildResult.routes_created ?? 0} rutas.
            </Alert>

            {Array.isArray(buildResult.orders_without_ring) && buildResult.orders_without_ring.length > 0 && (
              <Alert
                severity="warning"
                sx={{ mb: 1 }}
                action={
                  <Button size="small" color="inherit" onClick={handleViewSinAnillo}>
                    Ver detalle
                  </Button>
                }
              >
                <AlertTitle sx={{ fontWeight: "bold" }}>Órdenes sin anillo</AlertTitle>
                {buildResult.orders_without_ring.length} órdenes no pudieron asignarse a ningún anillo.
              </Alert>
            )}

            {Array.isArray(buildResult.failed_geocoding) && buildResult.failed_geocoding.length > 0 && (
              <Alert severity="warning">
                <AlertTitle sx={{ fontWeight: "bold" }}>Órdenes sin geocodificar</AlertTitle>
                No se pudo ubicar geográficamente:{" "}
                {buildResult.failed_geocoding
                  .map((o) => (typeof o === "string" ? o : o.correlative || o.zip))
                  .join(", ")}
              </Alert>
            )}
          </Box>
        )}
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h6">Rutas</Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Ver rutas del día"
            type="date"
            value={listDate}
            onChange={(e) => setListDate(e.target.value)}
            variant="filled"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <FormControl variant="filled" size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="select-route-status-label">Estado</InputLabel>
            <Select
              labelId="select-route-status-label"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="pendiente_aprobacion">Pendiente de aprobación</MenuItem>
              <MenuItem value="aprobada">Aprobada</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {routesLoading ? (
        <Loading />
      ) : routesListData.length === 0 ? (
        <Typography align="center" color="text.secondary" mt={4}>
          No hay rutas para el filtro seleccionado.
        </Typography>
      ) : (
        routesListData.map((route) => <RouteCard key={route.id} route={route} onActionComplete={refreshRoutes} />)
      )}

      {/* Órdenes sin anillo dialog */}
      <Dialog open={sinAnilloOpen} onClose={() => setSinAnilloOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Órdenes sin anillo asignado
          {sinAnilloData && (
            <Typography variant="body2" color="text.secondary">
              {sinAnilloData.total_ordenes} órdenes · {sinAnilloData.total_zips_sin_anillo} ZIPs sin cobertura
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {loadingSinAnillo ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          ) : grupos.length === 0 ? (
            <Typography color="text.secondary" mt={1}>
              No hay órdenes sin anillo para esta fecha.
            </Typography>
          ) : (
            grupos.map((grupo) => (
              <Box key={grupo.zip} mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Box>
                    <Typography fontWeight="bold">
                      ZIP {grupo.zip} — {grupo.city}, {grupo.state}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {grupo.ordenes.length} {grupo.ordenes.length === 1 ? "orden" : "órdenes"}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleOpenAddZip(grupo)}
                    sx={{ flexShrink: 0 }}
                  >
                    Agregar a anillo
                  </Button>
                </Box>
                <Box mt={1}>
                  {grupo.ordenes.map((orden) => (
                    <Box
                      key={orden.box_id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1}
                      sx={{ pl: 1, py: 0.5 }}
                    >
                      <Typography variant="body2">
                        {orden.correlative}
                        {orden.name ? ` — ${orden.name}` : ""}
                      </Typography>
                      <Button size="small" onClick={() => handleOpenAddToRoute(orden)} sx={{ flexShrink: 0 }}>
                        Agregar a ruta existente
                      </Button>
                    </Box>
                  ))}
                </Box>
                <Divider sx={{ mt: 1.5 }} />
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate("/GestionAnillos")} color="secondary">
            Crear anillo nuevo
          </Button>
          <Button onClick={() => setSinAnilloOpen(false)} color="inherit">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add ZIP to existing anillo sub-dialog */}
      <Dialog open={addZipOpen} onClose={() => setAddZipOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Agregar ZIP {selectedGroup?.zip} a anillo
          {selectedGroup && (
            <Typography variant="body2" color="text.secondary">
              {selectedGroup.city}, {selectedGroup.state}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          {loadingAnillos ? (
            <Box display="flex" justifyContent="center" p={1}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <FormControl variant="filled" size="small" fullWidth>
              <InputLabel id="select-anillo-label">Anillo</InputLabel>
              <Select
                labelId="select-anillo-label"
                value={selectedAnilloId}
                onChange={(e) => setSelectedAnilloId(e.target.value)}
              >
                {anillosList.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddZipOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleAddZipSubmit}
            variant="contained"
            color="secondary"
            disabled={addingZip || !selectedAnilloId}
          >
            {addingZip ? <CircularProgress size={20} color="inherit" /> : "Agregar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add order to existing route sub-dialog */}
      <Dialog open={addToRouteOpen} onClose={() => setAddToRouteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Agregar orden a ruta existente
          {selectedOrder && (
            <Typography variant="body2" color="text.secondary">
              {selectedOrder.correlative}
              {selectedOrder.name ? ` — ${selectedOrder.name}` : ""}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          {loadingRoutesForOrder ? (
            <Box display="flex" justifyContent="center" p={1}>
              <CircularProgress size={24} />
            </Box>
          ) : routesForOrder.length === 0 ? (
            <Typography color="text.secondary">
              No hay rutas pendientes de aprobación para el {targetDate}.
            </Typography>
          ) : (
            <FormControl variant="filled" size="small" fullWidth>
              <InputLabel id="select-target-route-order-label">Ruta destino</InputLabel>
              <Select
                labelId="select-target-route-order-label"
                value={targetRouteIdForOrder}
                onChange={(e) => setTargetRouteIdForOrder(e.target.value)}
              >
                {routesForOrder.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    Ruta #{r.id} — {r.ring_name || r.base_origin} ({r.total_orders} órdenes)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddToRouteOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleAddToRouteSubmit}
            variant="contained"
            color="secondary"
            disabled={addingToRoute || !targetRouteIdForOrder}
          >
            {addingToRoute ? <CircularProgress size={20} color="inherit" /> : "Agregar a ruta"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RuteoAutomatico;
